import {
  all,
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { normalize } from 'normalizr';
import {
  PapiDaDeviationGroupFolders,
  PapiDaDeviationGroups,
  PapiGallection,
  PapiUser,
  PapiDaSharedNetworkbar,
} from '@wix/da-papi-types';
import { selectors, withCursor, withOffset } from '../../Stream';
import { FetchAction, FetchType } from '../../Stream/base/types';
import { NotificationType } from '../../Notifications';
import { ResourceType } from '../../types/resource';
import {
  getCurrentUserId,
  getIsLoggedIn,
  getIsMobile,
} from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import { putEntities } from '../../redux/entities/actions';
import { deviationSchema } from '../../redux/normalizr/schemas/deviation';
import { requestPuppy } from '@wix/da-http-client';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { AddToGroupFolderSelectBiEvent, BiData } from '@wix/da-bi/pkg/events';
import { logBiEvent } from '../../biLogger/redux/saga';
import { getPageBiContext } from '../../biLogger/redux/selectors';
import {
  groupFoldersStreamType,
  groupsStreamType,
  recommendedGroupsStreamType,
  recommendedGroupsStreamId,
} from './constants';
import {
  FolderType,
  GroupAddedNotificationParams,
  GroupFolderStreamParams,
  GroupsStreamParams,
  RecommendedGroupsStreamParams,
  PapiDaDeviationGroupAddOrError,
} from './types';
import {
  addToGroupFolder,
  addToGroupFolderFail,
  addToGroupFolderSuccess,
  initGroupsPanel,
  initRecommendedGroups,
} from './actions';
import { getFolderStreamId, getGroupsStreamId } from './helpers';
import {
  addDeviationToGroupFolder,
  fetchGroupFolders,
  fetchRecommendedGroups,
} from './xhr';
import { userListSchema } from '../../redux/normalizr/schemas/user';
import { addNotificationToQueue } from '../../Notifications/redux/actionCreators';
import { getDeviationNotificationQueue } from '../../DeviationPage/redux/helpers';

const cursorFetchAction = withCursor.actions.STREAM__WITH_CURSOR__FETCH;
const offsetFetchAction = withOffset.actions.STREAM__WITH_OFFSET__FETCH;
export const GROUPS_PER_FETCH = 24;
export const GROUP_FOLDERS_PER_FETCH = 24;
// /developers/console/papi/da-deviation/dadeviation_groups/262c19bc5c3ed1ed9e6e1e55828095d4
const GROUPS_ENDPOINT_URL = 'groups';

export function* handleInit() {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    return;
  }

  // check if it's already init
  const streamId = getGroupsStreamId();
  const userId = yield select(getCurrentUserId);
  const existingStreamParams: GroupsStreamParams | undefined = yield select(
    selectors.getStreamParams,
    streamId
  );
  if (existingStreamParams && existingStreamParams.userId === userId) {
    // already initialized, we're good
    return;
  }

  yield put(
    withCursor.actions.initialize({
      streamId,
      itemsPerFetch: GROUPS_PER_FETCH,
      items: [],
      hasMore: true,
      streamParams: {
        type: groupsStreamType,
        userId,
      },
    })
  );
  yield put(withCursor.actions.fetch(streamId));
}

export function* handleInitRecommendedGroups() {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    return;
  }

  // check if it's already init
  const existingStreamParams: RecommendedGroupsStreamParams | undefined =
    yield select(selectors.getStreamParams, recommendedGroupsStreamId);

  if (existingStreamParams) {
    // already initialized, we're good
    return;
  }

  yield put(
    withCursor.actions.initialize({
      streamId: recommendedGroupsStreamId,
      itemsPerFetch: 3,
      items: [],
      hasMore: true,
      streamParams: {
        type: recommendedGroupsStreamType,
      },
    })
  );

  yield put(withCursor.actions.fetch(recommendedGroupsStreamId));
}

function* handleFetch({ streamId }: FetchAction) {
  const streamParams = yield select(selectors.getStreamParams, streamId);
  if (!streamParams) {
    return;
  }

  switch (streamParams.type) {
    case groupsStreamType:
      yield call(handleFetchGroups, streamId, streamParams);
      break;

    case groupFoldersStreamType:
      yield call(handleFetchFolders, streamId, streamParams);
      break;

    case recommendedGroupsStreamType:
      yield call(handleFetchRecommendedGroups, streamId, streamParams);
      break;
    default:
  }
}

function* handleFetchFolders(
  streamId: string,
  streamParams: GroupFolderStreamParams
) {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.ADD_TO_GROUP })
    );
    return;
  }

  yield delay(300);
  const result: PapiDaDeviationGroupFolders | false = yield call(
    fetchGroupFolders,
    streamParams.groupId,
    streamParams.folderType
  );

  if (!result) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch folders',
      })
    );
    return;
  }

  yield put(
    withOffset.actions.fetchSuccessDeduped({
      streamId,
      dedupeFunction: (item: PapiGallection, existingItems: PapiGallection[]) =>
        !existingItems.some(i => i.folderId === item.folderId),
      items: result.results,
      hasMore: false,
    })
  );
}

export function* handleFetchGroups(
  streamId: string,
  streamParams: GroupsStreamParams
) {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.ADD_TO_GROUP })
    );
    return;
  }

  yield delay(300);
  const cursor = yield select(withCursor.selectors.getCursor, streamId);
  const result: PapiDaDeviationGroups | false = yield call(
    requestPuppy,
    {
      method: 'get',
      url: GROUPS_ENDPOINT_URL,
      params: {
        cursor,
        limit: GROUPS_PER_FETCH,
      },
    },
    fakeFetchGroups,
    'dadeviation'
  );

  if (!result) {
    yield put(
      withCursor.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch groups',
      })
    );
    return;
  }

  // update groups stream, and init folder streams for all fetched groups
  yield all([
    put(
      withCursor.actions.fetchSuccessDeduped({
        streamId,
        dedupeFunction: (item: PapiUser, existingItems: PapiUser[]) =>
          !existingItems.some(i => i.userId === item.userId),
        items: result.results,
        hasMore: result.results?.length >= GROUPS_PER_FETCH,
        cursor: result.cursor,
      })
    ),
    ...result.results
      .map(group =>
        [FolderType.Gallery, FolderType.Collection].map(folderType => {
          const folderStreamId = getFolderStreamId(folderType, group.userId);
          const folderStreamParams: GroupFolderStreamParams = {
            type: groupFoldersStreamType,
            folderType,
            userId: streamParams.userId,
            groupId: group.userId,
          };

          return put(
            withOffset.actions.initialize({
              streamId: folderStreamId,
              itemsPerFetch: GROUP_FOLDERS_PER_FETCH,
              items: [],
              hasMore: true,
              streamParams: folderStreamParams,
            })
          );
        })
      )
      .reduce((flattened, current) => [...flattened, ...current], []),
  ]);
}

function fakeFetchGroups(): PapiDaDeviationGroups {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line global-require
    const groups: PapiUser[] = require('@wix/da-sample-data').groups;

    return {
      results: groups,
    };
  }
  return {
    results: [],
  };
}

export function* handleFetchRecommendedGroups(
  streamId: string,
  streamParams: RecommendedGroupsStreamParams
) {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.ADD_TO_GROUP })
    );
    return;
  }

  yield delay(300);
  const cursor = yield select(withCursor.selectors.getCursor, streamId);
  const result: PapiDaSharedNetworkbar | false = yield call(
    fetchRecommendedGroups,
    cursor
  );

  if (!result || result.recommendedGroups?.errorCode) {
    yield put(
      withCursor.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch recommended groups',
      })
    );
    return;
  }

  const { result: items, entities } = normalize(
    result.recommendedGroups?.groups ?? [],
    userListSchema
  );

  yield put(
    withCursor.actions.fetchSuccessDeduped({
      streamId,
      dedupeFunction: (item: PapiUser, existingItems: PapiUser[]) =>
        !existingItems.some(i => i.userId === item.userId),
      items,
      entities,
      hasMore: false, // recommended groups don't support pagination
      cursor: result.recommendedGroups?.cursor,
    })
  );
}

function* handleAddToGroup({
  payload: { group, deviation, groupFolderId, actionbarType, biData = {} },
}: ReturnType<typeof addToGroupFolder>) {
  const isLoggedIn = yield select(getIsLoggedIn);
  const biContext = yield select(getPageBiContext);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.ADD_TO_GROUP })
    );
    return;
  }

  const result: PapiDaDeviationGroupAddOrError = yield call(
    addDeviationToGroupFolder,
    groupFolderId,
    deviation.deviationId
  );

  const notification: GroupAddedNotificationParams = {
    success: result.success,
    error: result.errorDetails,
    needsVote: result.needsVote,
    deviation,
    group,
    groupFolderId,
    actionbarType,
  };

  if (!result.success) {
    yield put(addToGroupFolderFail(deviation.deviationId, groupFolderId));
  } else {
    yield put(
      addToGroupFolderSuccess(
        deviation.deviationId,
        groupFolderId,
        !!result.needsVote
      )
    );

    // need to update the stat if got added
    if (
      !result.needsVote &&
      typeof result.deviationGroupCount !== 'undefined' &&
      deviation.extended &&
      deviation.extended.stats
    ) {
      const updatedDeviation = {
        ...deviation,
        extended: {
          ...deviation.extended,
          stats: {
            ...deviation.extended.stats,
            groups: deviation.extended.stats.groups + 1,
          },
        },
      };
      const { entities } = normalize(updatedDeviation, deviationSchema);
      yield put(putEntities({ entities }));
    }
  }

  yield delay(1000);

  yield call(
    logBiEvent,
    BiData<AddToGroupFolderSelectBiEvent>({
      ...biContext,
      ...biData,
      evid: 342,
      deviationid: deviation.deviationId,
      groupid: group.useridUuid,
      typeid:
        groupFolderId.type === FolderType.Collection
          ? ResourceType.COLLECTION
          : ResourceType.GALLERY,
      itemid: groupFolderId.folderId,
    })
  );

  const isMobile = yield select(getIsMobile);
  yield put(
    addNotificationToQueue(
      isMobile ? 'default' : getDeviationNotificationQueue(deviation),
      NotificationType.AddToGroup,
      notification
    )
  );
}

export default function* groupsRootSaga() {
  yield all([
    takeEvery(initGroupsPanel, handleInit),
    takeEvery(initRecommendedGroups, handleInitRecommendedGroups),
    // groups stream is cursor-based, folders stream is offset-based
    takeEvery(cursorFetchAction, handleFetch),
    takeEvery(offsetFetchAction, handleFetch),
    takeLatest(addToGroupFolder, handleAddToGroup),
  ]);
}
